import { useEffect, useRef, useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import useDebouncedCallback from '../../../hooks/useDebouncedCallback';
import SearchInput from '../../generic/Forms/SearchInput';

import styles from './FossabotPrsSearch.module.less';

/** Long enough to swallow a burst of typing, short enough to still feel immediate. */
const SEARCH_DEBOUNCE_TIMEOUT = 300;

type Props = {
  /** The search as it stands on the URL. The box follows it whenever it changes. */
  search: string;
  /** Called with the search once typing has settled, never on every keystroke. */
  onSearchChange: (search: string) => void;
};

/**
 * Search box above the PR table. The input updates as fast as the user types
 * while only the settled value goes up to the page, which puts it in the URL and
 * hands it to the list request, so a search is server-side and linkable.
 */
export const FossabotPrsSearch: VoidFunctionComponent<Props> = ({ search, onSearchChange }) => {
  const [value, setValue] = useState(search);
  /** The last search this box sent up, so its own write to the URL does not read as an outside one. */
  const reportedSearch = useRef(search);
  /*
   * useDebouncedCallback captures its function once, on mount, so the report goes
   * through a ref rather than closing over the prop: reporting rewrites the URL,
   * and rewriting the URL is exactly what hands the page a fresh callback.
   */
  const onSearchChangeRef = useRef(onSearchChange);
  useEffect(() => {
    onSearchChangeRef.current = onSearchChange;
  }, [onSearchChange]);

  const reportSearch = useDebouncedCallback((nextSearch: string) => {
    reportedSearch.current = nextSearch;
    onSearchChangeRef.current(nextSearch);
  }, SEARCH_DEBOUNCE_TIMEOUT);

  /*
   * The URL can change under the box without a remount, since react-router keeps
   * this component in place for a navigation on the same route: clicking the
   * already-active PRs tab drops the search param, for one. Follow it, or the box
   * would go on showing a search the table has already stopped applying. A report
   * still in flight belongs to the search that was just replaced, so it is dropped.
   */
  useEffect(() => {
    if (search !== reportedSearch.current) {
      reportedSearch.current = search;
      reportSearch.cancel();
      setValue(search);
    }
  }, [search, reportSearch]);

  const onChange = (nextValue: string) => {
    setValue(nextValue);
    reportSearch(nextValue);
  };

  return (
    <div className={styles.search}>
      <SearchInput
        value={value}
        onChange={onChange}
        size="small"
        label="Search fossabot pull requests"
        placeholder="Search PR..."
        testId="fossabot-prs-search"
      />
    </div>
  );
};
